import React, { useRef, useState } from "react";
import {
  VStack,
  Button,
  HStack,
  Text,
  FormLabel,
  Grid,
  GridItem,
  Image,
  Tooltip,
  useToast,
  Box,
} from "@chakra-ui/react";
import { COLORS } from "../../../Helper/Colors";
import { CloudArrowUp } from "phosphor-react";
import { useFormik } from "formik";
import * as yup from "yup";
import DotSpinner from "../DotSpinner";
import ProfileImage from "../../../Assets/profile_placeholder.jpg";
import { FormInput } from "../UI";
import { useDispatch } from "react-redux";
import Api from "../../../Helper/ApiHandler";
import { login } from "../../../Redux/Auth/Actions";

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

export const EditProfileDetails = ({ profileData, setUpdate }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const uploadRef = useRef(null);
  const toast = useToast();
  const api = new Api();
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(profileData?.profilePhoto);

  const initialValue = {
    first_name: profileData.firstName || "",
    last_name: profileData.lastName || "",
    email: profileData.email || "",
    profile_image: null,
  };

  const schema = yup.object().shape({
    first_name: yup.string().required("First Name Is Required!"),
    last_name: yup.string().required("Last Name Is Required!"),
    email: yup
      .string()
      .email("Please Enter Valid Email!")
      .required("Email is Required!"),
    profile_image: yup
      .mixed()
      .notRequired()
      .test(
        "fileFormat",
        "Unsupported Format! Please upload .png, .jpg or .jpeg file type",
        (value) => {
          if (value === null) {
            return true;
          } else if (value) {
            return SUPPORTED_FORMATS.includes(value.type);
          } else {
            return false;
          }
        }
      )
      .test("fileSize", "File size must be less than 5 mb.", (value) => {
        if (value === null) {
          return true;
        } else if (value) {
          return value.size < 5242880;
        } else {
          return false;
        }
      }),
  });

  const handleSubmit = async (values) => {
    try {
      setBtnLoading(true);
      let formData = new FormData();
      formData.append("firstName", values.first_name);
      formData.append("lastName", values.last_name);

      if (uploadRef.current.files[0]) {
        formData.append("profileImage", uploadRef.current.files[0]);
      }
      const { data } = await api.put("auth/edit-profile", formData);
      dispatch(login({ ...data }));
      setBtnLoading(false);
      toast({
        description: "Your profile details has been updated.",
        status: "success",
        duration: 3000,
        position: "top",
      });
      setUpdate(false);
    } catch (error) {
      setBtnLoading(false);
      toast({
        description: "Error while updating profile.",
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: schema,
    onSubmit: handleSubmit,
  });

  const handleCancel = () => {
    formik.setValues(initialValue);
    setUpdate(false);
  };

  const handleUpload = () => {
    uploadRef.current.click();
  };
  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      let img = event.target.files[0];
      formik.setValues({ ...formik.values, profile_image: img });
      setProfile(URL.createObjectURL(img));
    }
  };

  return (
    <>
      <Box marginTop={"25px"} padding={4} borderRadius="lg" width={"100%"}>
        <form onSubmit={formik.handleSubmit}>
          <Text color={COLORS.dark} fontSize="xl" fontWeight={"medium"}>
            Personal Information
          </Text>
          <Box
            marginTop={"25px"}
            border={"1px solid"}
            borderColor={COLORS.gray}
            padding={4}
            borderRadius="lg"
            width={"100%"}
          >
            <input
              type="file"
              ref={uploadRef}
              accept="image/jpg, image/jpeg, image/png"
              hidden
              onChange={handleImageChange}
            />
            <Grid templateColumns="50% 50%" marginLeft={4} marginTop={4}>
              <GridItem w="100%" paddingRight={6} paddingTop={6}>
                <FormInput
                  onBlur={formik.handleBlur}
                  type="text"
                  name="first_name"
                  label="First Name"
                  placeholder="Enter First Name"
                  value={formik.values.first_name}
                  error={
                    !!formik.touched.first_name && formik.errors.first_name
                  }
                  errorMsg={formik.errors.first_name}
                  onChange={formik.handleChange}
                  isRequired={true}
                />
              </GridItem>
              <GridItem w="100%" paddingRight={6} paddingTop={6}>
                <FormInput
                  onBlur={formik.handleBlur}
                  type="text"
                  name="last_name"
                  label="Last Name"
                  placeholder="Enter Last Name"
                  value={formik.values.last_name}
                  error={!!formik.touched.last_name && formik.errors.last_name}
                  onChange={formik.handleChange}
                  isRequired={true}
                  errorMsg={formik.errors.last_name}
                />
              </GridItem>
              <GridItem w="100%" paddingRight={6} paddingTop={6}>
                <FormInput
                  onBlur={formik.handleBlur}
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter Email Id"
                  value={formik.values.email}
                  error={!!formik.touched.email && formik.errors.email}
                  onChange={formik.handleChange}
                  isRequired={true}
                  errorMsg={formik.errors.email}
                  isDisabled={true}
                />
              </GridItem>

              <GridItem w="100%" paddingRight={6} paddingTop={6}>
                <VStack alignItems="start" width={"72"}>
                  <FormLabel htmlFor="profile_image" margin={0}>
                    Upload Photo
                  </FormLabel>
                  <HStack style={{ alignItems: "center" }}>
                    <Image
                      height="64px"
                      width="64px"
                      borderRadius="3px"
                      src={!!profile ? profile : ProfileImage}
                      objectFit="cover"
                    />
                    <Button
                      size={"lg"}
                      variant="ghost"
                      leftIcon={
                        <CloudArrowUp
                          size={24}
                          color={COLORS.brand}
                          weight="duotone"
                        />
                      }
                      onClick={handleUpload}
                      type="button"
                      fontWeight={"light"}
                      textDecoration="underline"
                      textColor={COLORS.dark}
                    >
                      Upload Photo
                    </Button>
                  </HStack>
                </VStack>
                {!!formik.errors.profile_image && (
                  <HStack style={{ alignItems: "center" }}>
                    <Text fontSize={"sm"} color={COLORS.red}>
                      {formik.errors.profile_image}
                    </Text>
                  </HStack>
                )}
              </GridItem>
            </Grid>

            <HStack
              alignItems={"flex-start"}
              marginTop={8}
              marginLeft={4}
              spacing={6}
            >
              <Button
                size={"lg"}
                isLoading={btnLoading}
                spinner={
                  <DotSpinner
                    key={"campaign-manager-edit-basic-form"}
                    color={"#fff"}
                    size={8}
                  />
                }
                type="submit"
                colorScheme="brand"
                width="28"
                fontWeight={"normal"}
                marginBottom="6"
                backgroundColor={COLORS.brand}
              >
                SAVE
              </Button>
              <Button
                size={"lg"}
                fontWeight={"normal"}
                variant="outline"
                onClick={handleCancel}
                type="button"
                width="28"
                colorScheme="brand"
              >
                CANCEL
              </Button>
            </HStack>
          </Box>
        </form>
      </Box>
    </>
  );
};
